<template>
  <section class="mb-8">
    <h3
      class="font-roboto text-xl font-bold mb-2"
      id="required-cover-letter">
      {{ props.name }}
    </h3>
    <p class="mb-4">
      {{ props.acceptedFileList }}
    </p>
    <UploadedFileList
      :fileListType="props.fileType"
      :reorder="reOrder" />
    <FormInputFile
      ref="fileInput"
      :id="fileId"
      :multiple="true"
      :accept="props.acceptedFileTypes"
      @custom-input="fileChanged"
      :buttonDataTestId="'upload-' + fileId"
      :inline="true"
      :showSelectedFiles="false"
      class="mb-4">
    </FormInputFile>
    <InlineWarningMessage
      v-for="(err, idx) in localErrors"
      :key="err.fileName + '-' + idx"
      :errorMessage="err.text"
      :errorTestId="fileTypeErrorId"
    />
    <NotificationComponent
      v-if="hasError"
      :data-testid="`failed-to-upload-${props.fileType}`"
      class="mb-8"
      role="alert"
      variant="error"
      heading-text="Upload error"
      :heading-level="1">
      <template #body>
        <UploadErrorMessage
          :fileTypeLabel="fileTypeLabel"
          :dataTestId="props.fileType"
          :testId="props.fileType"
        />
      </template>
    </NotificationComponent>
  </section>
</template>

<script setup lang="ts">
import { SubmissionFileType, SubmissionFile } from '@/types'
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useFileUpload } from '@/composables/useFileUpload'
import UploadedFileList from '../upload/UploadedFileList.vue'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import FormInputFile from '../common/FormInputFile.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import { submissionFileTypeFormatter } from '../../services/formatters.ts'
import UploadErrorMessage from './UploadErrorMessage.vue'

const hasError = ref(false)

const props = defineProps<{
  name: string
  fileType: SubmissionFileType
  acceptedFileTypes: string[]
  acceptedFileList: string
}>()

const store = useStore()
const fileInput = ref<{ resetSelectedFiles: () => void } | null>(null)

const { uploadMultipleFiles, localErrors, clearErrors } = useFileUpload()

const fileId = props.name.toLowerCase().replace(/\s+/g, '-')

const fileTypeLabel = computed(() => submissionFileTypeFormatter(props.fileType))

const fileTypeErrorId = computed(() => {
  return props.fileType.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '') + '-upload'
})

const reOrder = computed(() => {
  return store.state.activeSubmission.files
    .filter((file: SubmissionFile) => file.type === props.fileType)
    .length > 1
})

const fileChanged = async (fileArray: FileList | File[] | null = null): Promise<void> => {
  const inputFiles = fileArray as FileList | File[] | null
  if (!inputFiles || !inputFiles[0]) {
    throw new Error('No file selected')
  }

  clearErrors()
  hasError.value = false
  await uploadMultipleFiles(
    inputFiles,
    props.fileType,
    props.name.replace(/\s+/g, ''),
    {
      successMessage: () => `Your ${props.name.toLowerCase()} has been successfully added.`,
      onFileSuccess: () => { hasError.value = false },
      onFileError: () => { hasError.value = true },
      onValidationError: (error, file) => {
        localErrors.value.push({
          fileName: file.name,
          text: error instanceof Error ? error.message : 'Validation error',
        })
      },
      onAllComplete: () => {
        if (fileInput.value) {
          fileInput.value.resetSelectedFiles()
        }
      },
    },
  )
}

const clearInlineErrors = () => {
  clearErrors()
  hasError.value = false
}

defineExpose({
  clearInlineErrors,
})
</script>
